import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { APPS, detectPlatform, storeUrl, trackStoreClick } from '../utils/storeLinks';
import './StoreCTA.css';

const STORE_LABEL = { app_store: 'the App Store', google_play: 'Google Play' };

// Platform is detected after mount so the prerendered HTML (desktop: both
// badges) and the first client render are identical and hydration is clean.
export function usePlatform() {
  const [platform, setPlatform] = useState('desktop');
  useEffect(() => { setPlatform(detectPlatform()); }, []);
  return platform;
}

function StoreLink({ app, store, placement, page, className, children, ariaLabel }) {
  return (
    <a
      href={storeUrl(app, store, placement, page)}
      target="_blank"
      rel="noopener"
      className={className}
      aria-label={ariaLabel}
      onClick={() => trackStoreClick({ app, store, placement, page })}
    >
      {children}
    </a>
  );
}

export function StoreBadges({ app, placement, page, only: onlyProp }) {
  const appName = APPS[app].name;
  const only = APPS[app].androidOnly ? 'google_play' : onlyProp;
  return (
    <div className="store-cta-badges">
      {only !== 'google_play' && (
        <StoreLink app={app} store="app_store" placement={placement} page={page} className="store-cta-badge"
          ariaLabel={`Download ${appName} on the App Store`}>
          <img src="/app-store-badge.svg" alt="Download on the App Store" width="135" height="45" loading="lazy" />
        </StoreLink>
      )}
      {only !== 'app_store' && (
        <StoreLink app={app} store="google_play" placement={placement} page={page} className="store-cta-badge"
          ariaLabel={`Get ${appName} on Google Play`}>
          <img src="/play-store-badge.png" alt="Get it on Google Play" width="152" height="45" loading="lazy" />
        </StoreLink>
      )}
    </div>
  );
}

// Platform-aware actions: one big primary button on phones (plus a quiet link to
// the other store), both official badges on desktop.
function StoreActions({ app, placement, page, platform, buttonText }) {
  if (platform === 'desktop') {
    return <StoreBadges app={app} placement={placement} page={page} />;
  }
  const primary = platform === 'android' || APPS[app].androidOnly ? 'google_play' : 'app_store';
  const secondary = primary === 'app_store' ? 'google_play' : 'app_store';
  return (
    <div className="store-cta-actions">
      <StoreLink app={app} store={primary} placement={placement} page={page} className="store-cta-button">
        {buttonText || `Get ${APPS[app].name} free`}
        <span className="store-cta-button-sub">on {STORE_LABEL[primary]}</span>
      </StoreLink>
      {!APPS[app].androidOnly && (
        <StoreLink app={app} store={secondary} placement={placement} page={page} className="store-cta-alt">
          Also on {STORE_LABEL[secondary]}
        </StoreLink>
      )}
    </div>
  );
}

/**
 * Store call-to-action.
 *  variant="card" - icon, headline, pitch and store buttons (content pages, home)
 *  variant="strip" - one compact row (under game players)
 *  variant="badges" - just the two badges (footer)
 */
function StoreCTA({ app = 'crowns', placement = 'cta', page, variant = 'card', headline, text, buttonText, id }) {
  const location = useLocation();
  const platform = usePlatform();
  const info = APPS[app];
  const pagePath = page || location.pathname;

  // Android-only apps have nothing to offer iPhone/iPad visitors.
  if (info.androidOnly && platform === 'ios') return null;

  if (variant === 'badges') {
    return <StoreBadges app={app} placement={placement} page={pagePath} />;
  }

  return (
    <aside id={id} className={`store-cta store-cta-${variant}`} aria-label={`Get the ${info.name} app`}>
      {info.icon && (
        <img className="store-cta-icon" src={info.icon} alt={`${info.name} app icon`} width="64" height="64" loading="lazy" />
      )}
      <div className="store-cta-body">
        <p className="store-cta-headline">{headline || `Play ${info.name} on your phone`}</p>
        {variant === 'card' && <p className="store-cta-text">{text || info.pitch}</p>}
      </div>
      <StoreActions app={app} placement={placement} page={pagePath} platform={platform} buttonText={buttonText} />
    </aside>
  );
}

export default StoreCTA;
